import { useTranslation } from "react-i18next";
import { cn, Icon } from "@nexus/ui";
import {
  CHANGELOG_AUTOMATION_PRESETS,
  presetFromSettings,
  type ChangelogAutomationPreset,
} from "@/features/changelog/lib/changelog-settings";
import { SettingsCard } from "../shared/SettingsCard";
import type { ChangelogSettingsCardProps } from "./types";

interface AutomationPresetCardProps extends ChangelogSettingsCardProps {
  /** A preset rewrites several fields at once, so it replaces the whole draft. */
  onApplyPreset: (preset: ChangelogAutomationPreset) => void;
}

/**
 * The only question most admins have — how automatic should this be — answered
 * with three presets. What users of the product see is deliberately not part of
 * any preset, so turning automation up can never be the change that starts
 * interrupting every user.
 */
export function AutomationPresetCard({
  draft,
  isReadOnly,
  onApplyPreset,
}: AutomationPresetCardProps) {
  const { t } = useTranslation();
  const activePreset = presetFromSettings(draft);

  return (
    <SettingsCard
      icon="sliders-horizontal"
      title={t("admin.changelog.settings.preset.title", "How automatic should this be?")}
      description={t(
        "admin.changelog.settings.preset.description",
        "One choice that sets the approval switches in the groups below. Your delays, thresholds and quiet hours are kept as they are. What users see inside the app is a separate control under Notes & announcements.",
      )}
    >
      <div
        role="radiogroup"
        aria-label={t("admin.changelog.settings.preset.title", "How automatic should this be?")}
        className="grid gap-2 px-5 py-4 sm:grid-cols-3"
      >
        {CHANGELOG_AUTOMATION_PRESETS.map((preset) => (
          <PresetOption
            key={preset}
            preset={preset}
            isActive={activePreset === preset}
            disabled={isReadOnly}
            onSelect={() => onApplyPreset(preset)}
          />
        ))}
      </div>
      {activePreset === null ? (
        <p className="m-0 px-5 pb-4 text-xs text-muted-foreground">
          {t(
            "admin.changelog.settings.preset.custom",
            "The switches below are a custom mix right now. Picking a level resets them to that level.",
          )}
        </p>
      ) : null}
    </SettingsCard>
  );
}

const PRESET_ICONS = {
  REVIEW_EVERYTHING: "shield",
  AUTO_WITH_GUARDRAILS: "clock",
  FULLY_AUTOMATIC: "sparkles",
} as const satisfies Record<ChangelogAutomationPreset, "shield" | "clock" | "sparkles">;

interface PresetOptionProps {
  preset: ChangelogAutomationPreset;
  isActive: boolean;
  disabled: boolean;
  onSelect: () => void;
}

/**
 * One of the three answers. A radio rather than a button because the three are
 * exclusive, and screen readers should say so.
 */
function PresetOption({ preset, isActive, disabled, onSelect }: PresetOptionProps) {
  const { t } = useTranslation();

  return (
    <button
      type="button"
      role="radio"
      aria-checked={isActive}
      disabled={disabled}
      onClick={onSelect}
      className={cn(
        "flex flex-col items-start gap-1 rounded-md border p-3 text-left transition-colors",
        "focus-visible:outline-2 focus-visible:outline-ring",
        isActive
          ? "border-primary bg-primary/5"
          : "border-border bg-background hover:border-primary/40",
        disabled && "cursor-not-allowed opacity-60",
      )}
    >
      <span className="flex items-center gap-2">
        <Icon
          name={PRESET_ICONS[preset]}
          size="sm"
          color={isActive ? "primary" : "muted"}
          aria-hidden
        />
        <span className="text-xs font-semibold text-foreground">
          {t(`admin.changelog.settings.presetOption.${preset}.title`, preset)}
        </span>
      </span>
      <span className="text-xs leading-relaxed text-muted-foreground">
        {t(`admin.changelog.settings.presetOption.${preset}.description`, "")}
      </span>
    </button>
  );
}
